import React, { useState } from 'react';
import { Table, Button, Select, Typography, Row, Col, Card, Space, message, Spin, notification } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import './ReportGenerator.scss';
import { reportApi } from '../../services/ReportApi';
import { IReportData, IReportItem } from '../../utils/Interfaces';
import { formatCurrency, formatDate } from '../../utils/FormatUtils';
import { useUser } from '../../contexts/UserContext';

import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

/**
 * Table columns configuration
 */
const columns: ColumnsType<IReportItem> = [
  { title: 'Order ID', dataIndex: 'orderId', key: 'orderId' },
  { title: 'Description', dataIndex: 'description', key: 'description' },
  { title: 'Order Date', dataIndex: 'orderDate', key: 'orderDate' },
  { title: 'Gross Income', dataIndex: 'grossIncome', key: 'grossIncome', align: 'right' },
  { title: 'Net Income', dataIndex: 'netIncome', key: 'netIncome', align: 'right' },
  { title: 'Platform Fees', dataIndex: 'platformFees', key: 'platformFees', align: 'right' },
];

const years = [2025, 2024];

const transformReportData = (data: IReportData[]): IReportItem[] => {
  return data.map((item, index) => ({
    key: index.toString(),
    orderId: item.order_id || '',
    description: item.description || '',
    orderDate: formatDate(item.order_date),
    grossIncome: formatCurrency(item.gross_income),
    netIncome: formatCurrency(item.net_income),
    platformFees: formatCurrency(item.platform_fee)
  }));
};

/**
 * ReportGenerator component for displaying and downloading annual reports
 */
const ReportGenerator: React.FC = () => {
  // State management
  const [year, setYear] = useState(years[0]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<IReportItem[]>([]);
  const [reportUrl, setReportUrl] = useState<string>('');
  const [reportFileName, setReportFileName] = useState<string>('');
  const [totals, setTotals] = useState({
    grossIncome: 0,
    netIncome: 0,
    platformFees: 0
  });

  // Get user data from context
  const { userProfile, userAddresses } = useUser();
  const primaryAddress = userAddresses?.find((addr: { is_primary?: boolean }) => addr.is_primary) || userAddresses?.[0];

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Export and get the report URL
      const exportResponse = await reportApi.exportAnnualReport(year);
      const reportFileUrl = exportResponse.data.data.data[0].url;
      const reportName = exportResponse.data.data.data[0].name;
      
      setReportUrl(reportFileUrl);
      setReportFileName(reportName);
      
      // Fetch the CSV data
      const csvData = await reportApi.getAnnualReportData(reportFileUrl);
      
      // Transform data for display
      const tableData = transformReportData(csvData);
      
      // Calculate totals
      const calculatedTotals = {
        grossIncome: csvData.reduce((sum, item) => sum + (item.gross_income || 0), 0),
        netIncome: csvData.reduce((sum, item) => sum + (item.net_income || 0), 0),
        platformFees: csvData.reduce((sum, item) => sum + (item.platform_fee || 0), 0)
      };
      
      setReportData(tableData);
      setTotals(calculatedTotals);
    } catch (error: any) {
      if (error.response && 
          error.response.status === 404 && 
          error.response.data && 
          error.response.data.message === "Failed to get annual reports to export") {
        // Set the table to no data default
        setReportData([]);
        setTotals({
          grossIncome: 0,
          netIncome: 0,
          platformFees: 0
        });
        setReportUrl('');
        setReportFileName('');
        message.info(`No annual reports available for ${year}`);
      } else {
        message.error('Failed to fetch report data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    fetchReportData();
  };

  const handleDownload = () => {
    if (reportUrl) {
      reportApi.downloadCsv(reportUrl, reportFileName);
      message.success('Report downloaded successfully');
    } else {
      message.warning('Please generate a report first');
    }
  };

  return (
    <div className="ReportGenerator">
      <Title level={2} className="report-title">Annual Tax Report</Title>

      <Card className="user-info">
        <Row gutter={24}>
          <Col span={12}>
            <Text strong>Full Name:</Text> {userProfile?.first_name} {userProfile?.last_name}<br />
            <Text strong>Email:</Text> {userProfile?.email}
          </Col>
          <Col span={12}>
            <Text strong>Business Name:</Text> {userProfile?.farm_info?.farm_name || 'N/A'}<br />
            <Text strong>Address:</Text> {primaryAddress ? 
              `${primaryAddress.apartment ? primaryAddress.apartment + ', ' : ''}${primaryAddress.street}, ${primaryAddress.city}, ${primaryAddress.province}` : 
              'No address available'}
          </Col>
        </Row>
      </Card>

      <div className="controls">
        <Space>
          <Text>Generated Year:</Text>
          <Select value={year} onChange={setYear} style={{ width: 120 }}>
            {years.map(y => (
              <Select.Option key={y} value={y}>{y}</Select.Option>
            ))}
          </Select>
        </Space>
        <Button className="preview-btn" onClick={handlePreview} loading={loading}>
          Preview Report
        </Button>
      </div>

      <div className="table-card">
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={reportData}
            pagination={false}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}><Text strong>Total</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right"><Text strong>{formatCurrency(totals.grossIncome)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right"><Text strong>{formatCurrency(totals.netIncome)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right"><Text strong>{formatCurrency(totals.platformFees)}</Text></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
            scroll={{ y: 300 }}
            rowKey="key"
            className="report-table"
          />
        </Spin>
      </div>

      <div className="actions">
        <Button 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={handleDownload} 
          className="download-btn"
          disabled={!reportUrl}
        >
          Download as CSV
        </Button>
      </div>
    </div>
  );
};

export default ReportGenerator;
